// Lane B4's words for the interview plan screen (Intake Part 2 as the interview-plan editor), the intake attendance
// dialog and the posting-strategy card: only the words the design system's tables do not already carry. Each quotes
// the line of the vendored glossary or design-system excerpts it is taken from, and each text also occurs in his
// glossary itself (the N-1 provenance rule of labels.test.mjs); nothing here is pending, so no word waits on him.
// The assessment card keeps its own table (views/plan/assessment/labels.ts); identical texts in both are allowed.

import { defineLabels, template, type LabelDef, type LabelSource } from "@/mc/labels";

const his = <const S extends string>(text: S, source: LabelSource, quote: string) =>
  ({ text, source, quote, status: "his" }) as const satisfies LabelDef;

const PLAN_ROW = "| Interview plan | \"Intake and interview-plan finalization\"; object \"InterviewPlan\"; \"plan cannot finalize with coverage gaps.\"";
const FRAMEWORK_ROW = "| TA-owned framework / level framework | \"Load TA-owned framework by level.\"";
const LEVEL_ROW = "| Level | \"Framework loads by level.\"";
const ROUND_ROW = "| Round / rounds | \"HM + Recruiter customize rounds, interviewers, ROOTS assignments, skills…\"";
const SKILLS_ROW = "| Required skills / skills | \"required skills must be covered\"; \"SkillAssignment\".";
const STANDARD_ROW = "**Quote / standard / descriptor / evidence**";
const COVERAGE_ROW = "| Coverage | \"Validate coverage before finalization.\" \"Coverage or data gap\" (Blocked). \"coverage-gap alerts\".";
const ROOTS_ROW = "\"All five ROOTS values and required skills must be covered before finalization.\"";
const LOOP_ROW = "| Final Interview Loop / Final Loop | \"Rename to Final Interview Loop and move scheduling to Coordinator.\"";
const ASSESSMENT_ROW = "| Assessment / case study | \"HM + Recruiter decide at intake: required?, type, stage, owner, reviewers and criteria.\"";
const ACK_ROW = "| Acknowledgment / accept / decline | \"Interviewers accept/decline.\"";
const INTAKE_ROW = "| Intake | \"Coordinator schedules required HM + Recruiter intake; HRBP optional.\"";
const STEP4_ROW = "| Intake and interview-plan finalization | ⚠ Intake call | HM + Recruiter (HRBP optional) |";
const HRBP_ROW = "\"HRBP optional\" at intake; required at debrief.";
const ATTENDANCE_LINE = "\"Intake attendance, assessment governance, scorecard SLA, concern flags and level-specific decision logic.\"";
const RECORD_WORDS = "primary **Record**";
const INTERNAL_ROW = "| Internal candidate | \"Internal candidates use same requisition/core governance with flexible interview plan when appropriate.";
const POSTING_ROW = "| Posting strategy | \"Preserve proposed vs current posting strategy and reason for change.\"";
const CONCURRENCY_ROW = "| Optimistic concurrency | \"Use optimistic concurrency or equivalent for mutable workflow records.\"";
const OVERRIDE_LINE = "\"Admin override only with reason and audit.\"";
const AUTHZ_ROW = "| Server-side authorization | \"Implement server-side authorization; UI hiding is insufficient.\"";
const JOINT_ROW = "| Finalize (plan / recommendation) | \"HM and Recruiter jointly edit/finalize.\"";
const ATTENDEES_WORDS = "*Attendees* · *free/busy* · *working hours* · *OOF* · *room* · *candidate availability*";
const ASSIGN_ROW = "| ROOTS/skill assignments (a round) | His \"Suggested ROOTS/skill assignments\" (above)";
const ASSIGN_GROUPS = "in two groups, **ROOTS** and **Skills**, pre-ticked by the framework";
const WHOLE_PANEL = "Coverage stays a rule of the whole panel: \"Cannot finalize if any required ROOTS value or skill is uncovered\" (DC-242).";
const REMOVE_LINE = "\"Remove recruiter self-claiming.\"";
const ROUND_LENGTH = "the round's format as the `seg` *virtual · onsite · hybrid* with no label and its length as a value (*60 minutes*)";

/**
 * The interview plan screen (BRD 3.4, CR-018/019/020): the page title (glossary §22 retires "Build Panel" and the
 * Intake Part 2 locator as a title, ADDENDUM §G), the framework banner, the planner's cards, the coverage gate's row
 * words by gate key, the rule sentences of the refused and empty states, the internal-candidate variant (CR-061) and
 * the read-only posting-strategy card (CR-014/015, DC-121).
 */
export const PLAN_WORDS = /* @__PURE__ */ defineLabels({
  interviewPlan: his("Interview plan"),
  taOwnedFramework: his("TA-owned framework"),
  frameworkLoadsByLevel: his("Framework loads by level."),
  rounds: his("Rounds"),
  requiredSkills: his("Required skills"),
  standard: his("Standard"),
  coverage: his("Coverage"),
  allFiveCovered: his("All five ROOTS values and required skills must be covered before finalization."),
  cannotFinalizeGaps: his("Plan cannot finalize with coverage gaps."),
  finalInterviewLoop: his("Final Interview Loop"),
  assessment: his("Assessment"),
  acknowledgment: his("Acknowledgment"),
  intakeRule: his("Coordinator schedules required HM + Recruiter intake; HRBP optional."),
  attendees: his("Attendees"),
  internalCandidate: his("Internal candidate"),
  flexibleInterviewPlan: his("Flexible interview plan"),
  internalRule: his("Internal candidates use same requisition/core governance with flexible interview plan when appropriate."),
  postingStrategy: his("Posting strategy"),
  postingRule: his("Preserve proposed vs current posting strategy and reason for change."),
  optimisticConcurrency: his("Use optimistic concurrency or equivalent for mutable workflow records."),
  adminOverride: his("Admin override only with reason and audit."),
  serverAuthorization: his("Implement server-side authorization; UI hiding is insufficient."),
  jointlyEditFinalize: his("HM and Recruiter jointly edit/finalize."),
  // F-163 / DC-242: every round card's two checkbox groups and the whole-panel rule of the coverage gate
  roundAssignments: his("ROOTS/skill assignments"),
  skills: his("Skills"),
  uncoveredRule: his("Cannot finalize if any required ROOTS value or skill is uncovered"),
  // F1 (26 Oct): a required skill's row carries Remove, his verb (the assessment card's reviewers and criteria use it too)
  remove: his("Remove"),
});

/** F-164 / DC-243: a round card shows its length as a value (*Duration* is a §22 word and is not drawn). */
export const PLAN_TEMPLATES = {
  minutes: /* @__PURE__ */ template(his("{n} minutes")),
};

/**
 * The intake attendance dialog (BRD 3.4 "HM + Recruiter required; HRBP optional."): its title, the HR Business
 * Partner's pill, the rule under the tiles and the commit. The trigger keeps V1's accessible name *Record as held* in
 * views/Intake.tsx (a V1 screen); the dialog's commit keeps *Record* (the walks' contract).
 */
export const HELD_WORDS = /* @__PURE__ */ defineLabels({
  intakeAttendance: his("Intake attendance"),
  hrbpOptional: his("HRBP optional"),
  hmRecruiterRequired: his("HM + Recruiter (HRBP optional)"),
  record: his("Record"),
});
